import { prefersReducedMotion } from '@theme/utilities';

/** Max tile size (px) per design spec; layer ranges are ratios of this. */
const TILE_SIZE_PX = 278;

/**
 * Vertical travel as a fraction of tile height at full scroll (sign = direction).
 * Values match design offsets at {@link TILE_SIZE_PX}px.
 * @type {readonly number[]}
 */
const LAYER_RANGE_RATIO = [
  150 / TILE_SIZE_PX,
  -130 / TILE_SIZE_PX,
  -43 / TILE_SIZE_PX,
  -170 / TILE_SIZE_PX,
  89 / TILE_SIZE_PX,
  -92 / TILE_SIZE_PX,
];

/** Global multiplier for parallax travel (0–1). */
const MOVEMENT_SCALE = 0.7;

/** Skip style writes when shift changes by less than this. */
const SHIFT_EPSILON = 0.0005;

/**
 * Tracks the parallax viewport in the window and applies translateY to each image tile.
 */
class ParallaxCtaComponent extends HTMLElement {
  /** @type {HTMLElement | null} */
  #viewport = null;

  /** @type {HTMLElement[]} */
  #blocks = [];

  /** @type {Float64Array} */
  #travels = new Float64Array(0);

  /** @type {number} */
  #rafId = 0;

  /** @type {boolean} */
  #isVisible = false;

  /** @type {boolean} */
  #listening = false;

  /** @type {number} */
  #lastShift = Number.NaN;

  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    this.#viewport = this.querySelector('.parallax-cta__viewport');
    if (!this.#viewport) {
      return;
    }

    this.#blocks = /** @type {HTMLElement[]} */ ([
      ...this.#viewport.querySelectorAll('.parallax-cta__layer .image-block'),
    ]);
    this.#travels = new Float64Array(this.#blocks.length);
    this.#cacheTravels();

    this.#intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        this.#isVisible = entry?.isIntersecting ?? false;
        this.#syncScrollListener();

        if (this.#isVisible) {
          this.#scheduleUpdate();
        } else {
          this.#viewport?.classList.remove('parallax-cta__viewport--active');
        }
      },
      { rootMargin: '10% 0px' }
    );
    this.#intersectionObserver.observe(this.#viewport);

    this.#resizeObserver = new ResizeObserver(() => {
      this.#cacheTravels();
      this.#lastShift = Number.NaN;

      if (this.#isVisible) {
        this.#scheduleUpdate();
      }
    });
    this.#resizeObserver.observe(this.#viewport);
  }

  disconnectedCallback() {
    this.#teardownScrollListener();
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    cancelAnimationFrame(this.#rafId);
    this.#rafId = 0;
    this.#clearTransforms();
  }

  #syncScrollListener() {
    if (this.#isVisible) {
      this.#setupScrollListener();
    } else {
      this.#teardownScrollListener();
    }
  }

  #setupScrollListener() {
    if (this.#listening) {
      return;
    }

    window.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#listening = true;
  }

  #teardownScrollListener() {
    if (!this.#listening) {
      return;
    }

    window.removeEventListener('scroll', this.#onScroll);
    this.#listening = false;
  }

  #onScroll = () => {
    this.#scheduleUpdate();
  };

  #scheduleUpdate() {
    if (this.#rafId) {
      return;
    }

    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = 0;
      this.#updateTransforms();
    });
  }

  #cacheTravels() {
    for (let i = 0; i < this.#blocks.length; i++) {
      const block = this.#blocks[i];
      const tileSize = block?.offsetHeight || TILE_SIZE_PX;
      const rangeRatio = LAYER_RANGE_RATIO[i] ?? 8 / TILE_SIZE_PX;
      // Precompute shift→px: y = shift * 2 * range * MOVEMENT_SCALE
      this.#travels[i] = 2 * rangeRatio * tileSize * MOVEMENT_SCALE;
    }
  }

  #clearTransforms() {
    this.#lastShift = Number.NaN;
    this.#viewport?.classList.remove('parallax-cta__viewport--active');

    for (const block of this.#blocks) {
      block.style.transform = '';
    }
  }

  #updateTransforms() {
    if (!this.#viewport || !this.#isVisible) {
      return;
    }

    if (prefersReducedMotion()) {
      this.#clearTransforms();
      return;
    }

    const rect = this.#viewport.getBoundingClientRect();
    const vh = window.innerHeight;
    const trackLength = vh + rect.height;

    // Fully off-screen (observer can lag with rootMargin)
    if (rect.bottom < 0 || rect.top > vh) {
      return;
    }

    const progress =
      trackLength > 0 ? Math.min(1, Math.max(0, (vh - rect.top) / trackLength)) : 0;
    const shift = progress - 0.5;

    if (Math.abs(shift - this.#lastShift) < SHIFT_EPSILON) {
      return;
    }

    this.#lastShift = shift;
    this.#viewport.classList.add('parallax-cta__viewport--active');

    for (let i = 0; i < this.#blocks.length; i++) {
      const y = shift * this.#travels[i];
      // translate3d keeps the layer on the compositor
      this.#blocks[i].style.transform = `translate3d(0, ${y}px, 0)`;
    }
  }
}

if (!customElements.get('parallax-cta-component')) {
  customElements.define('parallax-cta-component', ParallaxCtaComponent);
}
